import React from 'react';

export const uploadIssues = {
  id: 'troubleshooting-upload-issues',
  title: 'File Upload Problems',
  description: 'Troubleshoot common file upload issues and size limitations.',
  category: 'troubleshooting',
  lastUpdated: '2025-06-29',
  tags: ['upload', 'file-size', 'troubleshooting'],
  content: () => (
    <div>
      <div className="space-y-6">
        <p className="text-muted-foreground/90 leading-relaxed">
          Having trouble uploading your GLB/GLTF files? Here are the most common issues and 
          how to resolve them quickly.
        </p>
        
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-3">Upload Requirements</h2>
          <div className="bg-background-elevated rounded-xl border border-border/60 p-4 shadow-sm">
            <ul className="list-disc list-inside text-muted-foreground/90 space-y-2">
              <li><strong>File types:</strong> .glb, .gltf (with associated .bin and texture files)</li>
              <li><strong>Maximum size:</strong> 500MB per file</li>
              <li><strong>Supported browsers:</strong> <a href="https://www.google.com/chrome/" target="_blank" rel="noopener noreferrer" className="text-accent hover:text-accent/80 transition-colors duration-200">Chrome</a>,{' '}
                <a href="https://www.mozilla.org/firefox/" target="_blank" rel="noopener noreferrer" className="text-accent hover:text-accent/80 transition-colors duration-200">Firefox</a>,{' '}
                <a href="https://www.apple.com/safari/" target="_blank" rel="noopener noreferrer" className="text-accent hover:text-accent/80 transition-colors duration-200">Safari</a>,{' '}
                <a href="https://www.microsoft.com/edge" target="_blank" rel="noopener noreferrer" className="text-accent hover:text-accent/80 transition-colors duration-200">Edge</a> (latest versions)</li>
              <li><strong>Internet connection:</strong> Stable connection required for large files</li>
            </ul>
          </div>
        </div>
        
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-3">Common Upload Issues</h2>
          
          <div className="space-y-4">
            <div className="bg-warning/10 border border-warning/30 rounded-xl p-4">
              <h3 className="text-lg font-medium text-warning mb-2">Upload stalls or times out</h3>
              <p className="text-muted-foreground/90 mb-2">
                <strong>Possible causes:</strong> Slow internet, large file size, browser issues
              </p>
              <p className="text-muted-foreground/90">
                <strong>Solutions:</strong> Check your connection, try a different browser, reduce file size
              </p>
            </div>
            
            <div className="bg-warning/10 border border-warning/30 rounded-xl p-4">
              <h3 className="text-lg font-medium text-warning mb-2">"File type not supported"</h3>
              <p className="text-muted-foreground/90 mb-2">
                <strong>Possible causes:</strong> Wrong file extension, corrupted file
              </p>
              <p className="text-muted-foreground/90">
                <strong>Solutions:</strong> Ensure .glb/.gltf extension, re-export from 3D software
              </p>
            </div>
            
            <div className="bg-warning/10 border border-warning/30 rounded-xl p-4">
              <h3 className="text-lg font-medium text-warning mb-2">Upload button not responding</h3>
              <p className="text-muted-foreground/90 mb-2">
                <strong>Possible causes:</strong> JavaScript disabled, browser compatibility, ad blockers
              </p>
              <p className="text-muted-foreground/90">
                <strong>Solutions:</strong> Enable JavaScript, disable ad blockers, try incognito mode
              </p>
            </div>
          </div>
        </div>
        
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-3">Reducing File Size</h2>
          <p className="text-muted-foreground/90 leading-relaxed mb-3">
            If your file is too large, try these optimization techniques:
          </p>
          <ul className="list-disc list-inside text-muted-foreground/90 space-y-2 ml-4">
            <li>Compress textures to JPEG (for non-transparent areas) or <a href="https://developers.google.com/speed/webp" target="_blank" rel="noopener noreferrer" className="text-accent hover:text-accent/80 transition-colors duration-200">WebP</a></li>
            <li>Reduce texture resolution (e.g., 2048x2048 instead of 4096x4096)</li>
            <li>Simplify geometry by reducing polygon count</li>
            <li>Remove unused materials and textures</li>
            <li>Use <a href="https://google.github.io/draco/" target="_blank" rel="noopener noreferrer" className="text-accent hover:text-accent/80 transition-colors duration-200">Draco geometry compression</a> in your exporter</li>
            <li>Split large models into smaller parts</li>
          </ul>
        </div>
        
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-3">Browser-Specific Issues</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-background-elevated rounded-xl border border-border/60 p-4 shadow-sm">
              <h3 className="text-lg font-medium text-foreground mb-2">Chrome/Edge</h3>
              <ul className="text-muted-foreground/90 text-sm space-y-1">
                <li>• Clear browser cache</li>
                <li>• Disable extensions</li>
                <li>• Check for updates</li>
              </ul>
            </div>
            <div className="bg-background-elevated rounded-xl border border-border/60 p-4 shadow-sm">
              <h3 className="text-lg font-medium text-foreground mb-2">Firefox/Safari</h3>
              <ul className="text-muted-foreground/90 text-sm space-y-1">
                <li>• Enable JavaScript</li>
                <li>• Clear cookies</li>
                <li>• Try private browsing</li>
              </ul>
            </div>
          </div>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-foreground mb-3">Still Can't Upload?</h2>
          <p className="text-muted-foreground leading-relaxed">
            If you've tried all the troubleshooting steps above and are still having upload issues, 
            please contact me at <a href="mailto:stan@compress-glb.com" className="text-accent hover:text-accent/90">stan@compress-glb.com</a>.
            {' '}Please include:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mt-3">
            <li>Your browser name and version</li>
            <li>File size and format details</li>
            <li>Any error messages you see</li>
            <li>Screenshots of the upload interface</li>
            <li>Your internet connection speed (you can test at <a href="https://fast.com" target="_blank" rel="noopener noreferrer" className="text-accent hover:text-accent/90">fast.com</a>)</li>
          </ul>
          <p className="text-muted-foreground leading-relaxed mt-3">
            I'll help you get your files uploaded successfully.
          </p>
        </div>
      </div>
    </div>
  )
}; 